import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { Scale } from 'lucide-react'
import { getNarrativeConfig } from '../utils/category'
import { formatNumber, truncateText } from '../utils/format'
import { trackBusinessEvent } from '../utils/analytics'
import { useLanguage } from '../hooks/useLanguage'
import type { EventSummary } from '../types/event'
import type { TokenMarketPrice } from '../types/tokenMarket'

interface FollowToggleResult {
  success: boolean
  active_count: number
  max_count: number
  error?: string
}

interface LikeToggleResult {
  success: boolean
  liked: boolean
}

interface EventCardProps {
  event: EventSummary
  tokenPrices?: Record<string, TokenMarketPrice>
  date?: string
  narrative?: string
  isFollowed?: boolean
  isLiked?: boolean
  followCount?: number
  maxFollowCount?: number
  onToggleFollow?: (event: EventSummary) => Promise<FollowToggleResult>
  onToggleLike?: (event: EventSummary, liked: boolean, likesCountBefore: number) => Promise<LikeToggleResult>
  onFollowNotice?: (message: string) => void
  onBeforeNavigate?: () => void
  onClick?: () => void
}

export function EventCard({
  event,
  tokenPrices,
  date,
  narrative,
  isFollowed = false,
  isLiked = false,
  followCount = 0,
  maxFollowCount = 3,
  onToggleFollow,
  onToggleLike,
  onFollowNotice,
  onBeforeNavigate,
  onClick,
}: EventCardProps) {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const [liked, setLiked] = useState(false)
  const [likesCount, setLikesCount] = useState(event.engagement?.likes || 0)
  const [isLiking, setIsLiking] = useState(false)
  const [isFollowing, setIsFollowing] = useState(false)

  const config = getNarrativeConfig(event.narrative)

  useEffect(() => {
    setLiked(isLiked)
    setLikesCount((event.engagement?.likes || 0) + (isLiked ? 1 : 0))
  }, [event.event_id, event.engagement?.likes, isLiked])

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isLiking || !onToggleLike) return

    setIsLiking(true)
    try {
      const response = await onToggleLike(event, liked, likesCount)
      if (response.success) {
        setLiked(response.liked)
        setLikesCount((prev) => response.liked ? prev + 1 : Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Like failed:', error)
    } finally {
      setIsLiking(false)
    }
  }

  const handleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isFollowing || !onToggleFollow) return

    if (!isFollowed && followCount >= maxFollowCount) {
      onFollowNotice?.(t(
        `最多只能关注${maxFollowCount}个事件，请付费解锁更多功能`,
        `You can follow up to ${maxFollowCount} events. Please recharge to unlock more.`
      ))
      return
    }

    setIsFollowing(true)
    try {
      trackBusinessEvent(isFollowed ? 'unfollow_attempt' : 'follow_attempt', {
        event_id: event.event_id,
        narrative: event.narrative,
        follow_count_before: followCount,
        max_follow_count: maxFollowCount,
      }, event.event_id)
      const response = await onToggleFollow(event)

      if (response.success) {
        WebApp.HapticFeedback.notificationOccurred('success')
        trackBusinessEvent(isFollowed ? 'unfollow_success' : 'follow_success', {
          event_id: event.event_id,
          narrative: event.narrative,
          follow_count_after: response.active_count,
          max_follow_count: response.max_count,
        }, event.event_id)
      } else {
        onFollowNotice?.(response.error || t('关注失败', 'Failed to follow'))
      }
    } catch (error) {
      console.error('Follow failed:', error)
      onFollowNotice?.(t('关注失败，请稍后重试', 'Failed to follow. Please try again later.'))
    } finally {
      setIsFollowing(false)
    }
  }

  const handlePolicyAnalysis = (e: React.MouseEvent) => {
    e.stopPropagation()
    trackBusinessEvent('policy_analysis_click', {
      event_id: event.event_id,
      narrative: event.narrative,
      source_page: 'home',
    }, event.event_id)
    onBeforeNavigate?.()
    navigate(`/event/${event.event_id}/policy-analysis?date=${date || 'latest'}&narrative=${narrative || ''}`, { state: { event } })
  }

  const summary = event.background
    ? truncateText(event.background, 80)
    : event.summary
      ? truncateText(event.summary, 80)
      : null

  return (
    <div
      data-event-id={event.event_id}
      data-visible-event-id={event.event_id}
      data-narrative={event.narrative}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all max-w-3xl mx-auto"
    >
      <div onClick={onClick} className="p-3 cursor-pointer">
        <div className="relative flex items-center justify-between mb-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
            <span>{config.emoji}</span>
            <span>{config.shortName}</span>
          </span>

          <div className="absolute right-0 top-0 flex flex-col items-end gap-1 whitespace-nowrap">
            {event.token_tags?.slice(0, 2).map((token) => {
              const market = tokenPrices?.[token]
              return (
                <div
                  key={token}
                  className="w-fit px-2 py-1"
                >
                  <div className="text-left text-[11px] font-extrabold leading-4 tracking-wide text-gray-900 dark:text-gray-100">
                    {token}
                  </div>
                  <div className="mt-0.5 flex items-center justify-start gap-1">
                    <span className="min-w-0 text-left text-[10px] font-medium leading-4 tabular-nums text-gray-900 dark:text-gray-100">
                      {market ? formatTokenPrice(market.price_usd) : '—'}
                    </span>
                    <span className={`text-[10px] font-medium leading-4 tabular-nums ${
                      !market || market.change_24h === null
                        ? 'text-gray-900 dark:text-gray-100'
                        : market.change_24h >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-rose-600 dark:text-rose-400'
                    }`}>
                      {!market || market.change_24h === null
                        ? '—'
                        : `${market.change_24h >= 0 ? '↑' : '↓'}${Math.abs(market.change_24h).toFixed(2)}%`}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <h3 className={`font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2 leading-snug text-sm ${event.token_tags?.length ? 'pr-32' : ''}`}>
          {event.title || 'Untitled Event'}
        </h3>

        {summary && (
          <p className={`text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2 leading-relaxed ${event.token_tags?.length ? 'pr-32' : ''}`}>
            {summary}
          </p>
        )}

        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
          {event.engagement?.views && (
            <span className="flex items-center gap-0.5">
              <span>👁</span>
              <span className="font-medium">{formatNumber(event.engagement.views)}</span>
            </span>
          )}

          <span className="flex items-center gap-0.5">
            <span>{liked ? '❤️' : '♡'}</span>
            <span className="font-medium">{formatNumber(likesCount)}</span>
          </span>

          {event.engagement?.retweets && (
            <span className="flex items-center gap-0.5">
              <span>↗</span>
              <span className="font-medium">{formatNumber(event.engagement.retweets)}</span>
            </span>
          )}

          {!!event.total_mentions && event.total_mentions > 0 && (
            <span className="flex items-center gap-0.5">
              <span>@</span>
              <span className="font-medium">{event.total_mentions}</span>
            </span>
          )}

          {event.tweet_count > 0 && (
            <span className="flex items-center gap-0.5">
              <span>𝕏</span>
              <span className="font-medium">{event.tweet_count}</span>
            </span>
          )}
        </div>

        {event.mentioned_by_accounts && event.mentioned_by_accounts.length > 0 && (
          <div className="flex items-center gap-1 text-xs overflow-hidden">
            <span className="text-gray-400 dark:text-gray-500 shrink-0">{t('提及:', 'Mentioned:')}</span>
            <div className="flex items-center gap-1 overflow-hidden">
              {event.mentioned_by_accounts.slice(0, 3).map((account, i) => (
                <a
                  key={i}
                  href={`https://twitter.com/${account.screen_name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs hover:underline truncate max-w-[80px]"
                >
                  @{account.screen_name}
                </a>
              ))}
              {event.mentioned_by_accounts.length > 3 && (
                <span className="text-gray-400 dark:text-gray-500 text-xs">
                  +{event.mentioned_by_accounts.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="px-2 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 grid grid-cols-4 gap-1">
        <button
          onClick={handleLike}
          disabled={isLiking || !onToggleLike}
          data-analytics-id={liked ? 'like-remove' : 'like-add'}
          data-analytics-role="like"
          data-event-id={event.event_id}
          data-narrative={event.narrative}
          aria-label={liked ? t('取消点赞', 'Unlike') : t('点赞', 'Like')}
          className={`min-w-0 flex items-center justify-center gap-1 px-1 py-1 rounded text-[11px] font-medium transition-colors ${
            liked
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          <span>{liked ? '❤️' : '♡'}</span>
          <span>{liked ? t('已赞', 'Liked') : t('点赞', 'Like')}</span>
        </button>

        <button
          onClick={handleFollow}
          disabled={isFollowing || !onToggleFollow}
          className={`min-w-0 flex items-center justify-center gap-1 px-1 py-1 rounded text-[11px] font-medium transition-colors disabled:opacity-60 ${
            isFollowed
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          <span>📌</span>
          <span>{isFollowing ? t('处理中...', 'Processing...') : isFollowed ? t('已关注', 'Following') : t('帮我关注', 'Follow')}</span>
        </button>

        <button
          onClick={handlePolicyAnalysis}
          data-analytics-id="policy-analysis"
          aria-label={t('政策解读', 'Policy analysis')}
          className="min-w-0 flex items-center justify-center gap-1 px-1 py-1 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded text-[11px] font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
        >
          <Scale className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="leading-tight">{t('政策解读', 'Policy')}</span>
        </button>

        <button
          onClick={onClick}
          className="min-w-0 flex items-center justify-center gap-1 px-1 py-1 bg-blue-500 text-white rounded text-[11px] font-medium hover:bg-blue-600 transition-colors"
        >
          <span>{t('详情', 'Details')}</span>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  )
}

function formatTokenPrice(price: number): string {
  if (price >= 1000) {
    return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  }
  if (price >= 1) {
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  if (price >= 0.01) {
    return `$${price.toFixed(4)}`
  }
  return `$${price.toLocaleString(undefined, { maximumSignificantDigits: 4 })}`
}
